import React, {
    useRef,
    useState,
    useEffect,
    forwardRef,
    Fragment,
    useMemo,
    useImperativeHandle,
  } from "react";
  import ReactMarkdown from "react-markdown";
  import remarkGfm from "remark-gfm";
  import rehypeRaw from "rehype-raw";
  import mermaid from "mermaid";
  import ExcalidrawComponent from "./ExcalidrawComponent";
  import ProtectedAsset from "./ProtectedAsset";
  import styled from "styled-components";
  import { extractBlock, isBlock, isUnknownBlock, isBlockNonLanguage } from "../utils/EditorUtils";
  import fileService from "../services/fileService";
  
  const randomid = () => parseInt(String(Math.random() * 1e15), 10).toString(36);
  
  // Styled component to override Tailwind's reset styles
  const MarkdownContent = styled.div`
    /* Heading styles */
    h1, h2, h3, h4, h5, h6 {
      margin-top: 1em;
      margin-bottom: 0.5em;
      font-weight: bold;
      line-height: 1.2;
      color: ${props => props.isDark ? '#E5E7EB' : '#111827'};
    }
    
    h1 {
      font-size: 2em;
    }
    
    h2 {
      font-size: 1.5em;
    }
    
    h3 {
      font-size: 1.17em;
    }
    
    h4 {
      font-size: 1em;
    }
    
    h5 {
      font-size: 0.83em;
    }
    
    h6 {
      font-size: 0.67em;
    }
    
    /* Paragraph styles */
    p {
      margin-top: 0.5em;
      margin-bottom: 1em;
      line-height: 1.5;
      color: ${props => props.isDark ? '#E5E7EB' : '#374151'};
    }
    
    /* List styles */
    ul, ol {
      margin: 0.5em 0;
      padding-left: 2em;
      color: ${props => props.isDark ? '#E5E7EB' : '#374151'};
    }
    
    ul {
      list-style-type: disc;
    }
    
    ol {
      list-style-type: decimal;
    }
    
    li {
      margin: 0.25em 0;
      display: list-item;
      color: ${props => props.isDark ? '#E5E7EB' : '#374151'};
    }
    
    /* Nested lists */
    ul ul, ol ol, ul ol, ol ul {
      margin-top: 0;
      margin-bottom: 0;
    }
    
    /* Text formatting */
    strong, b {
      font-weight: bold;
      color: ${props => props.isDark ? '#E5E7EB' : '#111827'};
    }
    
    em, i {
      font-style: italic;
      color: ${props => props.isDark ? '#E5E7EB' : '#374151'};
    }
    
    /* Blockquotes */
    blockquote {
      margin: 1em 0;
      padding-left: 1em;
      border-left: 4px solid ${props => props.isDark ? '#6B7280' : '#e0e0e0'};
      color: ${props => props.isDark ? '#9CA3AF' : '#616161'};
      background-color: ${props => props.isDark ? 'rgba(107, 114, 128, 0.1)' : 'rgba(0, 0, 0, 0.02)'};
      border-radius: 0.25rem;
      padding: 0.75rem 1rem;
    }
    
    /* Code blocks */
    pre {
      margin: 1em 0;
      padding: 0.5em;
      background-color: ${props => props.isDark ? '#1F2937' : '#f5f5f5'};
      border: 1px solid ${props => props.isDark ? '#374151' : '#e0e0e0'};
      border-radius: 3px;
      overflow-x: auto;
      color: ${props => props.isDark ? '#E5E7EB' : '#111827'};
    }
    
    /* Inline code */
    code {
      padding: 0.2em 0.4em;
      background-color: ${props => props.isDark ? '#374151' : '#f5f5f5'};
      color: ${props => props.isDark ? '#F3F4F6' : '#111827'};
      border-radius: 3px;
      font-family: monospace;
      border: 1px solid ${props => props.isDark ? '#4B5563' : '#e0e0e0'};
    }
    
    /* Images */
    img {
      max-width: 100%;
      height: auto;
      margin: 1em 0;
      border-radius: 0.25rem;
      box-shadow: ${props => props.isDark ? '0 1px 3px rgba(0, 0, 0, 0.5)' : '0 1px 3px rgba(0, 0, 0, 0.12)'};
    }
    
    /* Horizontal rule */
    hr {
      margin: 1.5em 0;
      border: 0;
      border-top: 1px solid ${props => props.isDark ? '#4B5563' : '#e0e0e0'};
    }
    
    /* Links */
    a {
      color: ${props => props.isDark ? '#8B5CF6' : '#3273dc'};
      text-decoration: none;
      transition: color 0.15s ease-in-out;
    }
    
    a:hover {
      text-decoration: underline;
      color: ${props => props.isDark ? '#A78BFA' : '#2563eb'};
    }

    /* Tables */
    table {
      border-collapse: collapse;
      width: 100%;
      margin: 1em 0;
      background-color: ${props => props.isDark ? '#232936' : '#ffffff'};
      border: 1px solid ${props => props.isDark ? '#374151' : '#ddd'};
      border-radius: 0.375rem;
      overflow: hidden;
    }
    
    th, td {
      border: 1px solid ${props => props.isDark ? '#374151' : '#ddd'};
      padding: 8px;
      text-align: left;
      color: ${props => props.isDark ? '#E5E7EB' : '#111827'};
    }
    
    th {
      background-color: ${props => props.isDark ? '#374151' : '#f2f2f2'};
      font-weight: bold;
      color: ${props => props.isDark ? '#F9FAFB' : '#111827'};
    }
    
    tr:nth-child(even) {
      background-color: ${props => props.isDark ? 'rgba(55, 65, 81, 0.3)' : '#f9f9f9'};
    }

    /* Root color for all text elements */
    color: ${props => props.isDark ? '#E5E7EB' : '#374151'};
    
    /* Mermaid diagram dark mode support */
    .mermaid {
      background-color: ${props => props.isDark ? 'transparent' : 'transparent'};
    }
    
    /* Ensure all text inherits proper colors */
    * {
      color: inherit;
    }
  `;
  
  const MermaidRenderer = ({ children }) => {
    const domId = useMemo(() => `dome${randomid()}`, []);
    const [svgContent, setSvgContent] = useState('');
    const [error, setError] = useState(null);
    const code = typeof children === "string" ? children : children?.[0] || "";

    // Use effect with empty container reference
    useEffect(() => {
      let isMounted = true;
      
      // Initialize mermaid if needed
      try {
        mermaid.initialize({
          startOnLoad: false,
          theme: 'default'
        });
      } catch (err) {
        console.warn('Mermaid re-initialization:', err);
      }
      
      // Use mermaid.render with a try/catch
      try {
        mermaid
          .render(domId, code)
          .then(({ svg }) => {
            if (isMounted) {
              setSvgContent(svg);
              setError(null);
            }
          })
          .catch((err) => {
            console.error('Mermaid rendering error:', err);
            if (isMounted) {
              setError(`Error rendering diagram: ${err.message || 'Unknown error'}`);
              setSvgContent('');
            }
          });
      } catch (err) {
        console.error('Mermaid top-level error:', err);
        if (isMounted) {
          setError(`Error rendering diagram: ${err.message || 'Unknown error'}`);
        }
      }
      
      // Cleanup function
      return () => {
        isMounted = false;
      };
    }, [domId, code]);

    // Render the SVG content directly using dangerouslySetInnerHTML
    return (
      <div className="mermaid-diagram">
        {error ? (
          <pre style={{ color: 'red', whiteSpace: 'pre-wrap' }}>{error}</pre>
        ) : (
          <div 
            dangerouslySetInnerHTML={{ __html: svgContent }} 
            style={{ display: svgContent ? 'block' : 'none' }}
          />
        )}
      </div>
    );
  };
  // const extractExcalidrawRef = (content) => {
  //   if (!content) return null;
    
  //   const contentStr = typeof content === "string" ? content : content?.[0] || "";
    
  //   // Try different patterns to extract the reference ID
  //   // Pattern 1: ref="uuid"
  //   const refMatch = contentStr.match(/ref="([^"]+)"/);
  //   if (refMatch) return refMatch[1];
    
  //   // Pattern 2: ref='uuid'
  //   const refMatchSingle = contentStr.match(/ref='([^']+)'/);
  //   if (refMatchSingle) return refMatchSingle[1];
    
  //   // Pattern 3: Just a UUID in the format xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx
  //   const uuidMatch = contentStr.match(/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i);
  //   if (uuidMatch) return uuidMatch[1];
    
  //   // If content is very short and looks like a UUID itself, return it directly
  //   if (contentStr.length < 40 && contentStr.includes('-')) {
  //     return contentStr.trim();
  //   }
    
  //   return null;
  // };

  const extractExcalidrawRef = (content) => {
    console.log("content", content);
    if (!content) return null;
    
    // Handle both string and array content
    const contentStr = Array.isArray(content) ? content.join('\n') : content;
    console.log("contentStr", contentStr);
    // Find the reference ID in the content
    // const refMatch = contentStr.match(/ref="([^"]+)"/);
    const refMatch = contentStr.match(/ref=("?)([^\s">]+)/);

    console.log("refMatch", refMatch);
    return refMatch ? refMatch[2] : null;
  };
  
  const components = {
    // Add a simple pre handler
    pre({ children }) {
      // The actual styling and structure will be handled by the code component below
      // for non-special blocks, or directly by Mermaid/Excalidraw components.
      return <>{children}</>;
    },
    code({ node, inline, className = '', children, ...props }) {
      const match = /language-(\w+)/.exec(className || '');
      const language = match ? match[1] : null;

      // Handle Mermaid
      if (language === "mermaid") {
        // MermaidRenderer already includes necessary structure
        return <MermaidRenderer>{children}</MermaidRenderer>;
      }
     
      // Handle Excalidraw
      if (language === "excalidraw") {
        const refId = extractExcalidrawRef(children);
        console.log("refId", refId);
        if (refId) {
          // ExcalidrawComponent handles its own rendering
          return <ExcalidrawComponent initialData={refId} />;
        }
        console.error("No reference ID found in Excalidraw content:", children);
        // Render error state within a pre block for consistency
        return <pre><code>Error: Invalid Excalidraw reference</code></pre>;
      }

      // Handle inline code
      if (inline) {
        return <code className={className} {...props}>{children}</code>;
      }

      // Default rendering for other code blocks (wrap in pre)
      return (
        <pre className={className} {...props}>
          <code className={`language-${language || 'plaintext'}`}>{children}</code>
        </pre>
      );
    },
    // Handle images with ProtectedAsset for protected assets
    img({ src, alt, ...props }) {
      // Check if this is a protected asset URL
      if (src && (src.startsWith('/assets/') || src.includes('/files/assets/'))) {
        // Extract asset path for ProtectedAsset component
        let assetPath = src;
        if (src.includes('/files/assets/')) {
          // Extract path after /files/assets/
          assetPath = '/assets/' + src.split('/files/assets/')[1];
        }
        
        return (
          <ProtectedAsset
            src={assetPath}
            alt={alt || 'Protected image'}
            style={{ maxWidth: '100%', height: 'auto', margin: '1em 0' }}
            {...props}
          />
        );
      }
      
      // For external images, use regular img tag
      return (
        <img
          src={src}
          alt={alt}
          style={{ maxWidth: '100%', height: 'auto', margin: '1em 0' }}
          {...props}
        />
      );
    },
  };
  
  // Component to render a single markdown block, making it clickable
  const MarkdownBlock = ({ content, onClick }) => {
    // Ensure content is always a string for ReactMarkdown
    let outputContent = ""
    const displayContent = Array.isArray(content) ? content.join('\n') : content;
    if (isBlockNonLanguage(displayContent.split('\n')[0])) {
        outputContent = displayContent.slice(3, -3);
    
    } else {
        outputContent = displayContent;
    }
    return (
      <div onClick={onClick} style={{ cursor: "pointer", marginBottom: '1rem' }}> {/* Add margin */}
        <ReactMarkdown
          children={outputContent || ''} // Handle potentially empty content
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeRaw]}
          components={components}
        />
      </div>
    );
  };
  
  const NoteEditorComponent = forwardRef(({ value = '', onChange, className = '', templateSelected }, ref) => {
    const [editingBlockIndex, setEditingBlockIndex] = useState(null);
    const [editingContent, setEditingContent] = useState('');
    const [lastSelectedBlockIndex, setLastSelectedBlockIndex] = useState(0); // Track last selected index
    const textareaRef = useRef(null); // Ref for the textarea
    const componentMounted = useRef(true); // Track if component is mounted

    // Detect dark mode from document class or localStorage
    const [isDarkMode, setIsDarkMode] = useState(() => {
      // Wait for DOM to be ready and check in the correct order
      if (typeof window === 'undefined') return false;
      
      // First check if dark class is already present
      if (document.documentElement.classList.contains('dark')) {
        return true;
      }
      
      // Then check localStorage
      const savedDarkMode = localStorage.getItem('darkMode');
      if (savedDarkMode === 'true') {
        return true;
      }
      
      // Finally check system preference only if no saved preference
      if (savedDarkMode === null && window.matchMedia('(prefers-color-scheme: dark)').matches) {
        return true;
      }
      
      return false;
    });

    // Listen for dark mode changes
    useEffect(() => {
      // Initial sync with document class
      const initialDarkMode = document.documentElement.classList.contains('dark');
      if (initialDarkMode !== isDarkMode) {
        setIsDarkMode(initialDarkMode);
      }

      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
            const newDarkMode = document.documentElement.classList.contains('dark');
            setIsDarkMode(newDarkMode);
          }
        });
      });

      observer.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['class']
      });

      // Also listen for storage changes
      const handleStorageChange = (e) => {
        if (e.key === 'darkMode') {
          const newDarkMode = e.newValue === 'true';
          setIsDarkMode(newDarkMode);
          
          // Ensure document class is in sync
          if (newDarkMode) {
            document.documentElement.classList.add('dark');
          } else {
            document.documentElement.classList.remove('dark');
          }
        }
      };

      window.addEventListener('storage', handleStorageChange);

      // Cleanup
      return () => {
        observer.disconnect();
        window.removeEventListener('storage', handleStorageChange);
      };
    }, []); // Remove isDarkMode dependency to avoid loops

    // Effect to set mounted status
    useEffect(() => {
      componentMounted.current = true;
      return () => {
        componentMounted.current = false;
      };
    }, []);

    // Process content into blocks/lines using logic similar to ObsidianEditor
    const processedContent = useMemo(() => {
      if (!value) return [];
      const lines = value.split('\n');
      const result = [];
      let i = 0;
      //console.log("templateSelected", templateSelected);
      let blockToElementMap = {};
      if (templateSelected) {
      blockToElementMap = templateSelected.blocks.reduce((acc, block) => {
        if (block.element) {
          acc[`${block.icon} ${block.name}`] = block.element;
        }
        return acc;
      }, {});} else {
        blockToElementMap = {};
      }
      //console.log("blockToElementMap", blockToElementMap);
      while (i < lines.length) {
        if (isBlock(lines[i])) {
          // Extract the full block content including start/end markers
          const { content: blockLines, endIndex } = extractBlock(lines, i);
          result.push({
            type: 'block',
            content: blockLines, // Content is an array of lines
            startIndex: i,
            endIndex
          });
          i = endIndex + 1;
        } else {
          
          // Regular line
          result.push({
            type: 'line',
            content: lines[i], // Content is a string
            index: i
          });
          i++;
          if (blockToElementMap && false) {
          const line = lines[i];
          if (line){
            const blockKey = Object.keys(blockToElementMap).find(key => line.includes(key));
            if (blockKey) {
              //console.log("blockKey", blockKey);
              //console.log("blockToElementMap[blockKey]", blockToElementMap[blockKey]);
              //console.log("line", line);
            result.push(
              {
                type: 'line',
                content: blockToElementMap[blockKey], // Content is a string
                index: i
              }
            );
            i++;
            }
          }
        }
        }
      }
      // Handle trailing newline case: if the original value ends with \\n,
      // the last line might be empty. Ensure it's included if needed.
      if (value.endsWith('\\n') && (lines.length === 0 || lines[lines.length - 1] !== '')) {
           if (result.length === 0 || result[result.length - 1].type !== 'line' || (result[result.length - 1].type === 'line' && result[result.length - 1].content !== '')) {
              result.push({ type: 'line', content: '', index: lines.length });
           }
      } else if (value === '') {
          // Explicitly handle completely empty value to allow clicking
           result.push({ type: 'line', content: '', index: 0 });
      }

      return result;
    }, [value]);


    // Now that processedContent is defined, we can use it in useImperativeHandle
    useImperativeHandle(ref, () => ({
      getLastSelectedBlockIndex: () => lastSelectedBlockIndex,
      insertTextAtPosition: (text, index = lastSelectedBlockIndex) => {
        if (!componentMounted.current) return;
        
        if (index === null || index >= processedContent.length) {
          index = processedContent.length - 1;
        }
        
        const newProcessedContent = [...processedContent];
        const targetItem = newProcessedContent[index];

        if (!targetItem) return; // Safely handle case where target item doesn't exist

        // For block type, append to the content array
        if (targetItem.type === 'block') {
          const blockLines = [...targetItem.content]; // Create a fresh copy of the array
          // Insert before the closing backticks for code blocks
          const insertIndex = blockLines.length - 1;
          blockLines.splice(insertIndex, 0, text);
          targetItem.content = blockLines;
        } else {
          // For line type, append to the content string
          targetItem.content += (targetItem.content ? '\n' : '') + text;
        }

        // Reconstruct the full markdown value
        const newValue = newProcessedContent.map(item =>
          item.type === 'block' ? item.content.join('\n') : item.content
        ).join('\n');

        if (onChange) {
          onChange(newValue);
        }
      }
    }), [lastSelectedBlockIndex, processedContent, onChange]);

    const handleBlockClick = (index) => {
      setEditingBlockIndex(index);
      setLastSelectedBlockIndex(index); // Update last selected index
      const currentItem = processedContent[index];
      // Set editing content based on type (string for line, joined array for block)
      const initialEditContent = currentItem
        ? (currentItem.type === 'block' ? currentItem.content.join('\n') : currentItem.content)
        : '';
      setEditingContent(initialEditContent);
      
      // Set cursor at the end of content after render
      setTimeout(() => {
        if (textareaRef.current) {
          const contentLength = initialEditContent.length;
          textareaRef.current.setSelectionRange(contentLength, contentLength);
        }
      }, 0);
    };

    function addUpdateIfNeeded(content) {
      const lines = content.split('\n');
      const today = new Date().toISOString().slice(0, 10); // e.g., "2025-05-20"
    
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
    
        // Find the "- Updates:" line
        if (line === '- Updates:') {
          // Search for an empty update line right after
          for (let j = i + 1; j < lines.length; j++) {
            const trimmedLine = lines[j].trim();
    
            // Stop scanning if we hit another top-level section
            if (trimmedLine.startsWith('- ') && !trimmedLine.startsWith('- **')) {
              break;
            }
    
            // Match an empty update line like "  -"
            if (lines[j].trim() === '') {
              const indentation = "  "; // capture the exact spacing
              const prefixToAdd = `${indentation}- **${today}**: `;
              lines[j] = prefixToAdd; // Add update header with today
              return {
                content: lines.join('\n'),
                modifiedLine: j,
                prefixLength: prefixToAdd.length
              };
            }
          }
        }
     
      }
    
      // Return original content if no insertion is done
      return { content: content, modifiedLine: -1, prefixLength: 0 };
    }
    

    const handleEditChange = (e) => {
      console.log(e.target.value);
      
      // Save current cursor position
      const cursorPosition = textareaRef.current.selectionStart;
      console.log("cursorPosition", cursorPosition);
      
      // Get the updated content with prefixes if needed
      const { content: updatedContent, modifiedLine, prefixLength } = addUpdateIfNeeded(e.target.value);
      console.log(updatedContent);
      console.log(modifiedLine, prefixLength);
      
      // Set the content
      setEditingContent(updatedContent);
      
      // Calculate new cursor position if a prefix was added
      if (modifiedLine >= 0) {
        // Calculate position in the text where the modification happened
        const lines = e.target.value.split('\n');
        let positionBeforeModifiedLine = 0;
        
        // Sum up lengths of all lines before the modified line (including newline chars)
        for (let i = 0; i < modifiedLine; i++) {
          positionBeforeModifiedLine += lines[i].length + 1; // +1 for the newline
        }
        
        // If cursor was after the modification point, adjust it
        console.log("cursorPosition", cursorPosition, positionBeforeModifiedLine);
        if (cursorPosition >= positionBeforeModifiedLine) {
          // Position cursor after the added prefix
          const newPosition = positionBeforeModifiedLine + prefixLength;
          console.log("newPosition", newPosition);
          
          // Set cursor position after state update
          setTimeout(() => {
            if (textareaRef.current) {
              textareaRef.current.setSelectionRange(newPosition, newPosition);
            }
          }, 0);
        } else {
          // If cursor was before the modification, keep original position
          console.log("cursorPosition", cursorPosition);
          setTimeout(() => {
            if (textareaRef.current) {
              textareaRef.current.setSelectionRange(cursorPosition, cursorPosition);
            }
          }, 0);
        }
      } else {
        // No modification, just restore cursor
        setTimeout(() => {
          if (textareaRef.current) {
            textareaRef.current.setSelectionRange(cursorPosition, cursorPosition);
          }
        }, 0);
      }
      
      // Auto-resize textarea
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
        textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
      }
    };

    const handleBlur = () => {
      if (editingBlockIndex === null || editingBlockIndex >= processedContent.length) {
          // Reset if index is invalid (e.g., content changed drastically)
          setEditingBlockIndex(null);
          setEditingContent('');
          return;
      };

      // Don't reset lastSelectedBlockIndex here - keep it for toolbar operations
      //console.log('Editing Block Content', editingBlockIndex, editingContent);
      const newProcessedContent = [...processedContent];
      const editedItem = newProcessedContent[editingBlockIndex];

      // Update the content based on type
      if (editedItem.type === 'block') {
          // For blocks, split the edited content back into an array of lines
          editedItem.content = editingContent.split('\n');
      } else { // type === 'line'
          editedItem.content = editingContent;
      }

      // Reconstruct the full markdown value
      const newValue = newProcessedContent.map(item =>
          item.type === 'block' ? item.content.join('\n') : item.content
      ).join('\n');

      if (onChange && newValue !== value) { // Only call onChange if value actually changed
          onChange(newValue);
      }

      // Reset editing state
      setEditingBlockIndex(null);
      setEditingContent('');
    };

    // Handle key presses in the textarea
    const handleKeyDown = (e) => {
      // Handle arrow key navigation between blocks
      if ((e.key === 'ArrowUp' || e.key === 'ArrowDown') && !e.shiftKey) {
        const cursorPosition = textareaRef.current.selectionStart;
        const lines = editingContent.split('\n');
        const content = editingContent;
        
        // Calculate current line number based on cursor position
        let currentLineIndex = 0;
        let currentPosition = 0;
        
        for (let i = 0; i < lines.length; i++) {
          currentPosition += lines[i].length + 1; // +1 for the newline character
          if (currentPosition > cursorPosition) {
            currentLineIndex = i;
            break;
          }
        }
        
        // Navigate to previous block when pressing Up at the first line
        if (e.key === 'ArrowUp' && currentLineIndex === 0 && cursorPosition <= lines[0].length) {
          const previousBlockIndex = editingBlockIndex - 1;
          
          if (previousBlockIndex >= 0) {
            e.preventDefault();
            
            // Save current content without adding a new line
            saveContentForNavigation();
            
            // Move to the previous block
            setTimeout(() => {
              handleBlockClick(previousBlockIndex);
              // Cursor positioning is now handled in handleBlockClick
            }, 0);
            
            return;
          }
        }
        
        // Navigate to next block when pressing Down at the last line
        if (e.key === 'ArrowDown' && currentLineIndex === lines.length - 1) {
          // Check if cursor is at or after the last character of the current line
          const lastLineStart = content.lastIndexOf('\n') + 1;
          const isAtEndOfLine = cursorPosition >= lastLineStart + lines[lines.length - 1].length;
          
          if (isAtEndOfLine) {
            const nextBlockIndex = editingBlockIndex + 1;
            
            if (nextBlockIndex < processedContent.length) {
              e.preventDefault();
              
              // Save current content without adding a new line
              saveContentForNavigation();
              
              // Move to the next block
              setTimeout(() => {
                handleBlockClick(nextBlockIndex);
                // Cursor positioning is now handled in handleBlockClick
              }, 0);
              
              return;
            }
          }
        }
      }

      // Handle Tab key to add indentation instead of losing focus
      if (e.key === 'Tab') {
        e.preventDefault(); // Prevent default Tab behavior (focus shifting)
        
        // Get cursor position
        const cursorPosition = textareaRef.current.selectionStart;
        const selectionEnd = textareaRef.current.selectionEnd;
        
        // If there's a selection, we need to indent all selected lines
        if (cursorPosition !== selectionEnd) {
          // Get the selected text
          const selectedText = editingContent.substring(cursorPosition, selectionEnd);
          
          // If selection has multiple lines, indent each line
          if (selectedText.includes('\n')) {
            const textBeforeSelection = editingContent.substring(0, cursorPosition);
            const textAfterSelection = editingContent.substring(selectionEnd);
            
            // Indent each line with 2 spaces
            const indentedText = selectedText.replace(/^/gm, '  ');
            
            // Update content with indented text
            const newContent = textBeforeSelection + indentedText + textAfterSelection;
            setEditingContent(newContent);
            
            // Adjust selection to maintain the same selected lines
            setTimeout(() => {
              if (textareaRef.current) {
                textareaRef.current.setSelectionRange(
                  cursorPosition, 
                  cursorPosition + indentedText.length
                );
              }
            }, 0);
          } else {
            // Single line selection, just add 2 spaces at cursor position
            const newContent = 
              editingContent.substring(0, cursorPosition) + 
              '  ' + 
              editingContent.substring(cursorPosition);
              
            setEditingContent(newContent);
            
            // Set cursor position after the inserted spaces
            setTimeout(() => {
              if (textareaRef.current) {
                textareaRef.current.setSelectionRange(
                  cursorPosition + 2, 
                  cursorPosition + 2
                );
              }
            }, 0);
          }
        } else {
          // No selection, just add 2 spaces at cursor position
          const newContent = 
            editingContent.substring(0, cursorPosition) + 
            '  ' + 
            editingContent.substring(cursorPosition);
            
          setEditingContent(newContent);
          
          // Set cursor position after the inserted spaces
          setTimeout(() => {
            if (textareaRef.current) {
              textareaRef.current.setSelectionRange(
                cursorPosition + 2, 
                cursorPosition + 2
              );
            }
          }, 0);
        }
        
        return;
      }

      if (e.key === 'Enter' && !e.shiftKey) {
        // Get the current item being edited
        const currentEditItem = processedContent[editingBlockIndex];
        
        // Check if we're in a code block or special block
        const isInCodeBlock = currentEditItem?.type === 'block' && 
                             (currentEditItem.content[0]?.trim().startsWith('```'));
        
        // If in a code block, just insert a new line rather than splitting
        if (isInCodeBlock) {
          // Let the default Enter behavior happen (create newline within the block)
          return;
        }
        
        e.preventDefault(); // Prevent default Enter behavior for non-code blocks

        // Get cursor position
        const cursorPosition = textareaRef.current.selectionStart;
        
        // Split content at cursor position
        const contentBeforeCursor = editingContent.substring(0, cursorPosition);
        const contentAfterCursor = editingContent.substring(cursorPosition);

        // Create new processed content array
        const newProcessedContent = [...processedContent];
        const currentItem = newProcessedContent[editingBlockIndex];

        // Update current item with content before cursor
        if (currentItem.type === 'block') {
          // For blocks, split by newlines and update only the content, preserving markers
          const lines = contentBeforeCursor.split('\n');
          currentItem.content = lines;
        } else {
          // For simple lines
          currentItem.content = contentBeforeCursor;
        }

        // Insert new line item after current item with content after cursor
        const newItem = {
          type: 'line',
          content: contentAfterCursor,
          index: currentItem.type === 'block' ? currentItem.endIndex + 1 : currentItem.index + 1
        };

        // Insert new item and update indices
        newProcessedContent.splice(editingBlockIndex + 1, 0, newItem);
        
        // Update indices for all items after the inserted one
        for (let i = editingBlockIndex + 2; i < newProcessedContent.length; i++) {
          const item = newProcessedContent[i];
          if (item.type === 'block') {
            const blockLength = item.endIndex - item.startIndex;
            item.startIndex = newProcessedContent[i-1].index + 1;
            item.endIndex = item.startIndex + blockLength;
          } else {
            item.index = newProcessedContent[i-1].index + 1;
          }
        }

        // Reconstruct the full markdown value
        const newValue = newProcessedContent.map(item =>
          item.type === 'block' ? item.content.join('\n') : item.content
        ).join('\n');

        // Update the value
        if (onChange) {
          onChange(newValue);
        }

        // Set editing to the new line
        setEditingBlockIndex(editingBlockIndex + 1);
        setEditingContent(contentAfterCursor);
      }
    };

    // Handle paste events for image uploads
    const handlePaste = async (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      // Look for image items in the clipboard
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        
        if (item.type.indexOf('image') !== -1) {
          e.preventDefault(); // Prevent default paste behavior for images
          
          const file = item.getAsFile();
          if (!file) continue;

          try {
            // Show loading state (you might want to add a loading indicator)
            console.log('Uploading image...');
            
            // Upload the image using the file service
            const uploadResult = await fileService.uploadFile(file);
            
            if (uploadResult.success && uploadResult.data.uniqueUrl) {
              // Create markdown image syntax
              const imageMarkdown = `![${uploadResult.data.fileName || 'Uploaded image'}](${uploadResult.data.uniqueUrl})`;
              
              // Get current cursor position
              const cursorPosition = textareaRef.current.selectionStart;
              
              // Insert the image markdown at cursor position
              const newContent = 
                editingContent.substring(0, cursorPosition) + 
                imageMarkdown + 
                editingContent.substring(cursorPosition);
              
              setEditingContent(newContent);
              
              // Set cursor position after the inserted image markdown
              setTimeout(() => {
                if (textareaRef.current) {
                  textareaRef.current.setSelectionRange(
                    cursorPosition + imageMarkdown.length, 
                    cursorPosition + imageMarkdown.length
                  );
                  // Auto-resize textarea
                  textareaRef.current.style.height = 'auto';
                  textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
                }
              }, 0);
              
              console.log('Image uploaded and inserted successfully');
            } else {
              console.error('Upload failed:', uploadResult);
            }
          } catch (error) {
            console.error('Error uploading image:', error);
            // You might want to show a toast notification here
          }
          
          break; // Only handle the first image found
        }
      }
    };

    // Helper function to save content during navigation without adding extra newlines
    const saveContentForNavigation = () => {
      if (editingBlockIndex === null || editingBlockIndex >= processedContent.length) {
        // Reset if index is invalid
        setEditingBlockIndex(null);
        setEditingContent('');
        return;
      }

      const newProcessedContent = [...processedContent];
      const editedItem = newProcessedContent[editingBlockIndex];

      // Update the content based on type
      if (editedItem.type === 'block') {
        // For blocks, split the edited content back into an array of lines
        editedItem.content = editingContent.split('\n');
      } else { // type === 'line'
        editedItem.content = editingContent;
      }

      // Reconstruct the full markdown value without adding extra newlines
      const newValue = newProcessedContent.map(item =>
        item.type === 'block' ? item.content.join('\n') : item.content
      ).join('\n');

      if (onChange && newValue !== value) {
        onChange(newValue);
      }

      // Don't reset editing state as we're navigating to another block
    };

     // Effect to focus and auto-resize textarea when editing starts
     useEffect(() => {
        if (editingBlockIndex !== null && textareaRef.current) {
            textareaRef.current.focus();
            // Adjust height on mount and when content changes (via editingContent dependency)
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
        }
    }, [editingBlockIndex, editingContent]); // Add editingContent dependency for resize on paste etc.

    // Determine what to display
    const displayItems = processedContent.length > 0 ? processedContent : [{ type: 'line', content: '', index: 0 }];


    return (
      <div
        data-color-mode="light"
        style={{
          padding: "1rem",
          overflowY: "auto",  // Enable vertical scrolling
          height: "100%",      // Ensure it takes full available height
          maxHeight: "calc(100vh - 100px)" // Example max height, adjust as needed
        }}
      >
        <MarkdownContent isDark={isDarkMode}>
        {displayItems.map((item, index) => (
          <Fragment key={`${item.type}-${item.startIndex ?? item.index}`}> {/* More stable key */}
            {editingBlockIndex === index ? (
              <textarea
                ref={textareaRef}
                value={editingContent}
                onChange={handleEditChange}
                onBlur={(e) => {
                  handleBlur();
                  e.target.style.borderColor = isDarkMode ? '#374151' : '#ccc';
                  e.target.style.boxShadow = 'none';
                }}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                onFocus={(e) => {
                  e.target.style.borderColor = isDarkMode ? '#8B5CF6' : '#3B82F6';
                  e.target.style.boxShadow = isDarkMode ? '0 0 0 1px #8B5CF6' : '0 0 0 1px #3B82F6';
                }}
                style={{
                  width: '100%',
                  minHeight: '50px', // Minimum height
                  border: `1px solid ${isDarkMode ? '#374151' : '#ccc'}`,
                  backgroundColor: isDarkMode ? '#232936' : '#ffffff',
                  color: isDarkMode ? '#E5E7EB' : '#111827',
                  padding: '0.5em',
                  marginBottom: '1rem', // Match block margin
                  fontFamily: 'monospace', // Use monospace for markdown editing
                  fontSize: 'inherit', // Inherit font size
                  lineHeight: '1.5',   // Match paragraph line height
                  boxSizing: 'border-box', // Include padding and border in element's total width and height
                  resize: 'none', // Disable manual resizing by user
                  overflow: 'hidden', // Hide scrollbar until needed
                  borderRadius: '0.375rem',
                  transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out'
                }}
              />
            ) : (
              <MarkdownBlock
                // Pass the raw content (string or array) - MarkdownBlock handles joining array
                content={item.content}
                onClick={() => handleBlockClick(index)}
              />
            )}
          </Fragment>
        ))}
        {/* Render a clickable area if there's effectively no content to display and not editing */}
         {displayItems.length === 1 && displayItems[0].content === '' && editingBlockIndex === null && (
             <div
                onClick={() => handleBlockClick(0)}
                style={{ minHeight: '50px', border: '1px dashed #ccc', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#aaa' }}
            >
                Click to edit
            </div>
         )}
        </MarkdownContent>
      </div>
    );
  });
  
  export default NoteEditorComponent;
   